/**
 * One policy (Salesforce Opportunity). The stage track and, once sold, the post-sale band say
 * where it stands; the fields keep the org's page-layout order (spec 2.2) and edit in place;
 * the commission card shows how Expected Commission was worked out. On a call with the
 * customer, the live-call bar puts the monthly premium and next draft in reach.
 *
 * Expected Commission, Agent's Comp, Deducted and the cancellation stamp are the rules' to set
 * (A1, A3–A5); they read as plain text, and an admin's correction is its own form.
 */
import {
  BUSINESS_TYPES,
  CALL_TYPES,
  CANCELLATION_REASONS,
  COMMISSION_TIERS,
  LOSS_REASONS,
  nextDraftDate,
  partyRoleLabel,
  POST_SALE_STATUSES,
  POLICY_STAGES,
  policyStageLabel,
  SALE_STAGES,
  toUsDate,
  type Policy,
  type PolicyStage,
} from "@tlcrm/shared";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router";

import { ApiError, errorMessage } from "../api/client";
import {
  usePicklists,
  usePolicy,
  useRecalculatePolicy,
  useStageHistory,
  useTasks,
  useUpdatePolicy,
  useUsers,
} from "../api/hooks";
import { useMe } from "../auth/session";
import { History } from "../components/History";
import { Icon } from "../components/Icon";
import { InlineField } from "../components/InlineField";
import { StageTrack, PostSaleBand } from "../components/StageTrack";
import { TaskRows } from "../components/TaskList";
import { Timeline } from "../components/Timeline";
import { useToast } from "../components/Toasts";
import {
  CommissionFlag,
  ErrorState,
  FieldBox,
  PostSaleMark,
  Select,
  StageMark,
} from "../components/ui";
import { useForm } from "../forms/useForm";
import { dial } from "../lib/dial";
import { money, when } from "../lib/format";
import { useNow } from "../lib/useNow";
import { useRecordKey } from "../shell/useShortcuts";
import { LiveCallBar } from "./record/LiveCallBar";

/** Salesforce's stage probabilities. */
const PROBABILITY: Record<PolicyStage, number> = {
  qualification: 10,
  needs_analysis: 35,
  proposal: 75,
  issued: 50,
  closed_won: 100,
  closed_lost: 0,
};

const yesNo = (v: boolean) => (v ? "Yes" : "No");

function Fv({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  const empty = value === null || value === undefined || value === "";
  return (
    <div className="fv">
      <dt>{label}</dt>
      <dd className={empty ? "empty-v" : mono ? "mono" : undefined}>{empty ? "–" : value}</dd>
    </div>
  );
}

export function PolicyRecord() {
  const { id = "" } = useParams();
  const policy = usePolicy(id);
  if (policy.isError)
    return (
      <div className="page">
        <ErrorState error={policy.error} onRetry={() => void policy.refetch()} />
      </div>
    );
  if (policy.isPending) return <div className="record-head" style={{ height: 160 }} />;
  return <Loaded p={policy.data} />;
}

function Loaded({ p }: { p: Policy }) {
  const { can } = useMe();
  const toast = useToast();
  const now = useNow(60_000);
  const [tab, setTab] = useState<"details" | "history">("details");
  const [correcting, setCorrecting] = useState(false);
  const recalc = useRecalculatePolicy(p.id);
  const openTasks = useTasks({ assignee: "any", status: "open", contactId: p.customerId });
  const policyTasks = (openTasks.data ?? []).filter((t) => t.policyId === p.id);
  const today = new Date(now).toLocaleDateString("en-CA");
  const nextDraft = nextDraftDate(p.draftDay, p.effectiveDate, today);
  const sold = p.stage === "closed_won" || p.stage === "issued";

  useRecordKey({
    d: () => p.phone && dial(p.phone),
    n: () => document.getElementById(`note-${p.id}`)?.focus(),
  });

  return (
    <>
      <LiveCallBar
        contactIds={[p.customerId, ...p.parties.map((x) => x.contactId)]}
        phones={[p.phone]}
        about={{ policyId: p.id }}
        facts={
          <>
            <span>
              <b className="mono">{money(p.monthlyPremium, true)}</b> a month
            </span>
            <span>
              Next draft <b className="mono">{nextDraft ? toUsDate(nextDraft) : "–"}</b>
            </span>
            {p.policyNumber && (
              <span>
                No. <b className="mono">{p.policyNumber}</b>
              </span>
            )}
          </>
        }
      />
      <div className="record-head">
        <div className="record-crumb">
          <Link to="/policies">Policies</Link>
          <Icon name="chevR" />
          <Link to={`/contacts/${p.customerId}`}>{p.customerName}</Link>
          <Icon name="chevR" />
          <span>{p.name}</span>
        </div>
        <div className="record-title">
          <span className="face face-policy">
            <Icon name="briefcase" />
          </span>
          <div>
            <h1>{p.name}</h1>
            <div className="facts">
              <span className="id-mark id-policy">Policy</span>
              {p.carrier && <span>{p.carrier}</span>}
              {p.product && <span>{p.product}</span>}
              <span className="sep" />
              <span>Owner {p.owner.fullName}</span>
            </div>
          </div>
          <div className="actions">
            {p.phone && (
              <button className="btn" onClick={() => dial(p.phone!)} title="Dial on the desk (D)">
                <Icon name="phoneOut" /> Dial
              </button>
            )}
            {can("admin:commission") && !correcting && (
              <button className="btn btn-ghost" onClick={() => setCorrecting(true)}>
                <Icon name="edit" /> Correct commission
              </button>
            )}
          </div>
        </div>
        <div className="record-track">
          <StageTrack stage={p.stage} />
          {p.stage === "closed_won" && (
            <PostSaleBand
              status={p.postSaleStatus}
              effectiveDate={p.effectiveDate}
              nextDraft={nextDraft}
              monthly={p.monthlyPremium}
              cancellationDate={p.cancellationDate}
              cancellationReason={p.cancellationReason}
            />
          )}
        </div>
        <div className="highlights">
          <div>
            <span className="eyebrow">Total Premium</span>
            <div className="v mono">{money(p.totalPremium, true)}</div>
          </div>
          <div>
            <span className="eyebrow">A month</span>
            <div className="v mono">{money(p.monthlyPremium, true)}</div>
          </div>
          <div>
            <span className="eyebrow">{sold ? "Next draft" : "Date Sold"}</span>
            <div className="v mono">
              {sold ? (nextDraft ? toUsDate(nextDraft) : "–") : toUsDate(p.dateSold)}
            </div>
          </div>
          <div>
            <span className="eyebrow">Expected Commission</span>
            <div className="v mono">{money(p.expectedCommission, true)}</div>
          </div>
          <div>
            <span className="eyebrow">Commission Paid</span>
            <div className="v">{yesNo(p.commissionPaid)}</div>
          </div>
        </div>
        {can("audit:read") && (
          <div className="tabs" role="tablist">
            <button
              className="tab"
              role="tab"
              aria-selected={tab === "details"}
              onClick={() => setTab("details")}
            >
              Details
            </button>
            <button
              className="tab"
              role="tab"
              aria-selected={tab === "history"}
              onClick={() => setTab("history")}
            >
              Field history
            </button>
          </div>
        )}
      </div>
      <div className="record-body main-first">
        <div className="stack">
          {p.commissionStatus && p.commissionStatus !== "calculated" && (
            <div className="banner banner-ochre">
              <Icon name="alert" />
              <span>
                <CommissionFlag status={p.commissionStatus} />{" "}
                {p.commissionStatus === "no_rule"
                  ? "No active rule covers this carrier, policy type, tier and age. Expected Commission keeps its last value until one does."
                  : "Expected Commission needs Total Premium, Carrier Sold, Policy Type, DOB and the owner's tier."}
              </span>
              {can("admin:commission") && (
                <button
                  className="btn btn-sm"
                  disabled={recalc.isPending}
                  onClick={() =>
                    recalc.mutate(undefined, {
                      onSuccess: () => toast("Recalculated."),
                      onError: (err) => toast(errorMessage(err), { error: true }),
                    })
                  }
                >
                  Recalculate
                </button>
              )}
            </div>
          )}
          {correcting ? (
            <EditPolicy p={p} onDone={() => setCorrecting(false)} />
          ) : (
            <PolicyFields p={p} />
          )}
          {tab === "history" ? <History entity="policy" id={p.id} /> : <StageHistory id={p.id} />}
        </div>
        <div className="stack">
          <CommissionCard p={p} />
          <div className="card">
            <div className="card-head">
              <h3>Open activities</h3>
              <span className="mono t3">{policyTasks.length}</span>
            </div>
            {policyTasks.length ? (
              <TaskRows tasks={policyTasks} now={now} />
            ) : (
              <div className="rel-empty">Nothing open on this policy.</div>
            )}
          </div>
          <div className="card">
            <div className="card-head">
              <h3>People on this policy</h3>
            </div>
            <div className="rel">
              {p.parties.map((x) => (
                <Link key={x.id} to={`/contacts/${x.contactId}`} className="rel-item">
                  <div className="t">
                    {x.fullName}
                    {x.sharePercent !== null && (
                      <span className="mono t3" style={{ marginLeft: "auto", fontWeight: 400 }}>
                        {x.sharePercent}%
                      </span>
                    )}
                  </div>
                  <div className="s">
                    <span>{partyRoleLabel(x.role)}</span>
                    {x.relationship && <span>{x.relationship}</span>}
                  </div>
                </Link>
              ))}
            </div>
          </div>
          <Timeline kind="policies" id={p.id} />
        </div>
      </div>
    </>
  );
}

const RULES = "Set by the commission rules";

/** The org's fields in its order, each edited where it is read. */
function PolicyFields({ p }: { p: Policy }) {
  const { can } = useMe();
  const update = useUpdatePolicy(p.id);
  const policy = usePolicy(p.id);
  const picklists = usePicklists();
  const users = useUsers();
  const toast = useToast();
  const qc = useQueryClient();
  /** The version as the cache has it now, so "Keep mine" after a clash saves on theirs. */
  const version = () => qc.getQueryData<Policy>(["policy", p.id])?.version ?? p.version;

  /** One field's props: how to save it, and how to read it back after a clash. */
  const field = (
    key: keyof Policy & string,
    raw: (x: Policy) => string,
    toBody: (v: string) => Record<string, unknown>,
  ) => ({
    value: raw(p),
    save: async (v: string) => {
      await update.mutateAsync({ version: version(), ...toBody(v) });
      toast("Saved.");
    },
    refresh: async () => {
      const fresh = await policy.refetch();
      return fresh.data ? raw(fresh.data) : raw(p);
    },
    key,
  });
  const str = (k: keyof Policy & string) =>
    field(
      k,
      (x) => String(x[k] ?? ""),
      (v) => ({ [k]: v }),
    );
  const nullable = (k: keyof Policy & string) =>
    field(
      k,
      (x) => String(x[k] ?? ""),
      (v) => ({ [k]: v || null }),
    );
  const amount = (k: keyof Policy & string) =>
    field(
      k,
      (x) => text(x[k] as number | null),
      (v) => ({ [k]: num(v) }),
    );
  const day = (k: keyof Policy & string) =>
    field(
      k,
      (x) => toUsDate(x[k] as string | null),
      (v) => ({ [k]: v }),
    );
  const flag = (k: keyof Policy & string) =>
    field(
      k,
      (x) => String(Boolean(x[k])),
      (v) => ({ [k]: v === "true" }),
    );

  const stageOptions = POLICY_STAGES.filter(([s]) => SALE_STAGES.includes(s) || s === p.stage);
  const carriers = (picklists.data?.carriers ?? []).filter((c) => c.active || c.id === p.carrierId);
  const products = (picklists.data?.products ?? []).filter((c) => c.active || c.id === p.productId);
  const strip = ({ key: _k, ...rest }: ReturnType<typeof field>) => {
    void _k;
    return rest;
  };

  return (
    <div className="card">
      <div className="card-head">
        <h3>Policy information</h3>
        <span className="card-sub">Click a value to change it</span>
      </div>
      <div className="card-body">
        <dl className="fields" style={{ margin: 0 }}>
          <InlineField label="Policy Name" {...strip(str("name"))} />
          {can("records:reassign") ? (
            <InlineField
              label="Owner"
              kind="select"
              options={(users.data ?? []).map((u) => [u.id, u.fullName] as const)}
              display={p.owner.fullName}
              {...strip(
                field(
                  "owner",
                  (x) => x.owner.id,
                  (v) => ({ ownerId: v }),
                ),
              )}
            />
          ) : (
            <Fv label="Owner" value={p.owner.fullName} />
          )}
          <Fv
            label="Customer"
            value={<Link to={`/contacts/${p.customerId}`}>{p.customerName}</Link>}
          />
          <InlineField label="Date Sold" kind="date" mono {...strip(day("dateSold"))} />
          <InlineField label="Former Carrier" {...strip(str("formerCarrier"))} />
          <InlineField
            label="Stage"
            kind="select"
            options={stageOptions}
            display={<StageMark stage={p.stage} />}
            {...strip(str("stage"))}
          />
          <InlineField
            label="Carrier Sold"
            kind="select"
            options={carriers.map((c) => [c.id, c.name] as const)}
            empty="Not chosen"
            display={p.carrier}
            {...strip(nullable("carrierId"))}
          />
          <Fv label="Probability" value={`${PROBABILITY[p.stage]}%`} mono />
          <InlineField
            label="Total Coverage"
            kind="number"
            mono
            display={p.totalCoverage !== null ? money(p.totalCoverage) : null}
            {...strip(amount("totalCoverage"))}
          />
          <InlineField label="Policy Number" mono {...strip(str("policyNumber"))} />
          <InlineField
            label="Policy Effective Date"
            kind="date"
            mono
            {...strip(day("effectiveDate"))}
          />
          <InlineField
            label="Post-Sale Status"
            kind="select"
            options={POST_SALE_STATUSES}
            display={<PostSaleMark status={p.postSaleStatus} />}
            {...strip(str("postSaleStatus"))}
          />
          <InlineField label="Modified?" kind="check" {...strip(flag("modified"))} />
          <InlineField
            label="Cancellation Reason"
            kind="select"
            options={CANCELLATION_REASONS}
            empty="None"
            {...strip(nullable("cancellationReason"))}
          />
          <InlineField label="DOB" kind="date" mono {...strip(day("insuredDob"))} />
          <InlineField
            label="Cancellation Date"
            kind="date"
            mono
            {...strip(day("cancellationDate"))}
          />
          <InlineField
            label="Call Type"
            kind="select"
            options={CALL_TYPES}
            empty="None"
            {...strip(nullable("callType"))}
          />
          <Fv
            label="Agent's Comp"
            value={<span title={RULES}>{p.agentComp ? `${p.agentComp}%` : null}</span>}
            mono
          />
          <InlineField
            label="Total Premium"
            kind="number"
            mono
            display={p.totalPremium !== null ? money(p.totalPremium, true) : null}
            {...strip(amount("totalPremium"))}
          />
          <InlineField
            label="Expected Commission"
            locked={RULES}
            mono
            value={text(p.expectedCommission)}
            display={p.expectedCommission !== null ? money(p.expectedCommission, true) : null}
            save={async () => undefined}
            refresh={async () => ""}
          />
          <InlineField label="Rewrite" kind="check" {...strip(flag("rewrite"))} />
          <InlineField label="Commission Paid" kind="check" {...strip(flag("commissionPaid"))} />
          <Fv label="Customer's Full Name" value={p.customerName} />
          <InlineField
            label="Commission Amount"
            kind="number"
            mono
            display={p.commissionAmount !== null ? money(p.commissionAmount, true) : null}
            {...strip(amount("commissionAmount"))}
          />
          <InlineField
            label="Policy Type"
            kind="select"
            options={products.map((c) => [c.id, c.name] as const)}
            empty="Not chosen"
            display={p.product}
            {...strip(nullable("productId"))}
          />
          <Fv label="Deducted" value={<span title={RULES}>{yesNo(p.deducted)}</span>} />
          <InlineField label="Notes" {...strip(str("notes"))} />
          <Fv
            label="Deducted Amount"
            value={
              p.deductedAmount !== null ? (
                <span title={RULES}>{money(p.deductedAmount, true)}</span>
              ) : null
            }
            mono
          />
          <InlineField label="Date of Rewrite" kind="date" mono {...strip(day("rewriteDate"))} />
          <InlineField
            label="Type"
            kind="select"
            options={BUSINESS_TYPES}
            empty="None"
            {...strip(nullable("businessType"))}
          />
          {p.stage === "closed_lost" && (
            <InlineField
              label="Loss Reason"
              kind="select"
              options={LOSS_REASONS}
              empty="None"
              {...strip(nullable("lossReason"))}
            />
          )}
        </dl>
      </div>
    </div>
  );
}

/** How Expected Commission was worked out (A1): premium × factor × rule rate, to the cap. */
function CommissionCard({ p }: { p: Policy }) {
  const rule = p.commissionRule;
  return (
    <div className="card commission-card">
      <div className="card-head">
        <h3>Commission</h3>
        {p.commissionStatus && p.commissionStatus !== "calculated" && (
          <CommissionFlag status={p.commissionStatus} short />
        )}
      </div>
      <div className="card-body">
        <span className="eyebrow">Expected Commission</span>
        <div className="commission-v mono">{money(p.expectedCommission, true)}</div>
        <dl className="calc">
          <dt>Total Premium</dt>
          <dd className="mono">{money(p.totalPremium, true)}</dd>
          <dt>× premium factor</dt>
          <dd className="mono">{p.premiumFactor}</dd>
          <dt>× rule rate</dt>
          <dd className="mono">{rule ? `${rule.payableRate}%` : "–"}</dd>
          <dt>Cap</dt>
          <dd className="mono">
            {!rule ? "–" : rule.noCap ? "No cap" : money(rule.maxPayoutCap, true)}
          </dd>
          <dt>Agent's Comp</dt>
          <dd className="mono">{p.agentComp ? `${p.agentComp}%` : "–"}</dd>
        </dl>
        <p className="card-note">
          {rule ? (
            <>Rule: {rule.name}</>
          ) : (
            "No rule matched yet: carrier, policy type, tier and age choose one."
          )}
        </p>
      </div>
    </div>
  );
}

const num = (s: string) => (s.trim() ? Number(s.replace(/[$,]/g, "")) : null);
const text = (n: number | null) => (n === null ? "" : String(n));

function EditPolicy({ p, onDone }: { p: Policy; onDone: () => void }) {
  const { can } = useMe();
  const update = useUpdatePolicy(p.id);
  const picklists = usePicklists();
  const users = useUsers();
  const toast = useToast();
  const f = useForm({
    name: p.name,
    ownerId: p.owner.id,
    dateSold: toUsDate(p.dateSold),
    formerCarrier: p.formerCarrier ?? "",
    stage: p.stage as string,
    carrierId: p.carrierId ?? "",
    totalCoverage: text(p.totalCoverage),
    policyNumber: p.policyNumber ?? "",
    effectiveDate: toUsDate(p.effectiveDate),
    postSaleStatus: p.postSaleStatus as string,
    modified: p.modified,
    cancellationReason: (p.cancellationReason ?? "") as string,
    insuredDob: toUsDate(p.insuredDob),
    cancellationDate: toUsDate(p.cancellationDate),
    callType: (p.callType ?? "") as string,
    totalPremium: text(p.totalPremium),
    rewrite: p.rewrite,
    commissionPaid: p.commissionPaid,
    commissionAmount: text(p.commissionAmount),
    productId: p.productId ?? "",
    notes: p.notes ?? "",
    rewriteDate: toUsDate(p.rewriteDate),
    businessType: (p.businessType ?? "") as string,
    lossReason: (p.lossReason ?? "") as string,
    agentComp: text(p.agentComp),
    deducted: p.deducted,
    deductedAmount: text(p.deductedAmount),
  });
  const v = f.values;
  const admin = can("admin:commission");

  const save = async () => {
    try {
      await update.mutateAsync({
        version: p.version,
        name: v.name,
        ...(v.ownerId !== p.owner.id && { ownerId: v.ownerId }),
        dateSold: v.dateSold,
        formerCarrier: v.formerCarrier,
        stage: v.stage,
        carrierId: v.carrierId || null,
        totalCoverage: num(v.totalCoverage),
        policyNumber: v.policyNumber,
        effectiveDate: v.effectiveDate,
        postSaleStatus: v.postSaleStatus,
        modified: v.modified,
        cancellationReason: v.cancellationReason || null,
        insuredDob: v.insuredDob,
        cancellationDate: v.cancellationDate,
        callType: v.callType || null,
        totalPremium: num(v.totalPremium),
        rewrite: v.rewrite,
        commissionPaid: v.commissionPaid,
        commissionAmount: num(v.commissionAmount),
        productId: v.productId || null,
        notes: v.notes,
        rewriteDate: v.rewriteDate,
        businessType: v.businessType || null,
        lossReason: v.stage === "closed_lost" ? v.lossReason || null : null,
        ...(admin && {
          ...(num(v.agentComp) !== p.agentComp && v.agentComp && { agentComp: num(v.agentComp) }),
          ...(v.deducted !== p.deducted && { deducted: v.deducted }),
          ...(num(v.deductedAmount) !== p.deductedAmount && {
            deductedAmount: num(v.deductedAmount),
          }),
        }),
      });
      toast("Saved.");
      onDone();
    } catch (err) {
      if (err instanceof ApiError && err.code === "stale_version") f.setFormError(err.message);
      else f.fail(err);
    }
  };

  const stageOptions = POLICY_STAGES.filter(([s]) => SALE_STAGES.includes(s) || s === p.stage);
  const carriers = (picklists.data?.carriers ?? []).filter((c) => c.active || c.id === p.carrierId);
  const products = (picklists.data?.products ?? []).filter((c) => c.active || c.id === p.productId);

  return (
    <form
      className="card"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <div className="card-head">
        <h3>Edit policy</h3>
      </div>
      <div className="card-body stack">
        {f.formError && <div className="banner banner-brick">{f.formError}</div>}
        <div className="form-grid">
          <FieldBox label="Policy Name" htmlFor="ep-name" error={f.errors.name}>
            <input id="ep-name" {...f.text("name")} />
          </FieldBox>
          <FieldBox label="Owner" htmlFor="ep-owner" error={f.errors.ownerId}>
            {can("records:reassign") ? (
              <Select
                id="ep-owner"
                value={v.ownerId}
                onChange={f.set("ownerId")}
                options={(users.data ?? []).map((u) => [u.id, u.fullName] as const)}
              />
            ) : (
              <input id="ep-owner" className="field" value={p.owner.fullName} readOnly />
            )}
          </FieldBox>
          <FieldBox label="Date Sold" htmlFor="ep-sold" error={f.errors.dateSold}>
            <input id="ep-sold" placeholder="MM-DD-YYYY" {...f.text("dateSold", "mono")} />
          </FieldBox>
          <FieldBox label="Former Carrier" htmlFor="ep-former" error={f.errors.formerCarrier}>
            <input id="ep-former" {...f.text("formerCarrier")} />
          </FieldBox>
          <FieldBox label="Stage" htmlFor="ep-stage" error={f.errors.stage}>
            <Select
              id="ep-stage"
              value={v.stage}
              onChange={(x) => f.set("stage")(x || p.stage)}
              options={stageOptions}
            />
          </FieldBox>
          <FieldBox label="Carrier Sold" htmlFor="ep-carrier">
            <Select
              id="ep-carrier"
              value={v.carrierId}
              onChange={f.set("carrierId")}
              options={carriers.map((c) => [c.id, c.name] as const)}
              empty="Not chosen"
            />
          </FieldBox>
          <FieldBox label="Total Coverage" htmlFor="ep-cover" error={f.errors.totalCoverage}>
            <input id="ep-cover" inputMode="decimal" {...f.text("totalCoverage", "mono")} />
          </FieldBox>
          <FieldBox label="Policy Number" htmlFor="ep-number" error={f.errors.policyNumber}>
            <input id="ep-number" {...f.text("policyNumber", "mono")} />
          </FieldBox>
          <FieldBox label="Policy Effective Date" htmlFor="ep-eff" error={f.errors.effectiveDate}>
            <input id="ep-eff" placeholder="MM-DD-YYYY" {...f.text("effectiveDate", "mono")} />
          </FieldBox>
          <FieldBox label="Post-Sale Status" htmlFor="ep-post">
            <Select
              id="ep-post"
              value={v.postSaleStatus}
              onChange={(x) => f.set("postSaleStatus")(x || "active")}
              options={POST_SALE_STATUSES}
            />
          </FieldBox>
          <FieldBox label="Cancellation Reason" htmlFor="ep-creason">
            <Select
              id="ep-creason"
              value={v.cancellationReason}
              onChange={f.set("cancellationReason")}
              options={CANCELLATION_REASONS}
              empty="None"
            />
          </FieldBox>
          <FieldBox label="Cancellation Date" htmlFor="ep-cdate" error={f.errors.cancellationDate}>
            <input
              id="ep-cdate"
              placeholder="Stamped when cancelled"
              {...f.text("cancellationDate", "mono")}
            />
          </FieldBox>
          <FieldBox label="DOB" htmlFor="ep-dob" error={f.errors.insuredDob}>
            <input id="ep-dob" placeholder="MM-DD-YYYY" {...f.text("insuredDob", "mono")} />
          </FieldBox>
          <FieldBox label="Call Type" htmlFor="ep-calltype">
            <Select
              id="ep-calltype"
              value={v.callType}
              onChange={f.set("callType")}
              options={CALL_TYPES}
              empty="None"
            />
          </FieldBox>
          <FieldBox label="Total Premium" htmlFor="ep-prem" error={f.errors.totalPremium}>
            <input id="ep-prem" inputMode="decimal" {...f.text("totalPremium", "mono")} />
          </FieldBox>
          <FieldBox label="Policy Type" htmlFor="ep-product">
            <Select
              id="ep-product"
              value={v.productId}
              onChange={f.set("productId")}
              options={products.map((c) => [c.id, c.name] as const)}
              empty="Not chosen"
            />
          </FieldBox>
          <FieldBox
            label="Commission Amount (actual paid)"
            htmlFor="ep-camount"
            error={f.errors.commissionAmount}
          >
            <input id="ep-camount" inputMode="decimal" {...f.text("commissionAmount", "mono")} />
          </FieldBox>
          <FieldBox label="Date of Rewrite" htmlFor="ep-rdate" error={f.errors.rewriteDate}>
            <input id="ep-rdate" placeholder="MM-DD-YYYY" {...f.text("rewriteDate", "mono")} />
          </FieldBox>
          <FieldBox label="Type" htmlFor="ep-btype">
            <Select
              id="ep-btype"
              value={v.businessType}
              onChange={f.set("businessType")}
              options={BUSINESS_TYPES}
              empty="None"
            />
          </FieldBox>
          {v.stage === "closed_lost" && (
            <FieldBox label="Loss Reason" htmlFor="ep-loss" error={f.errors.lossReason}>
              <Select
                id="ep-loss"
                value={v.lossReason}
                onChange={f.set("lossReason")}
                options={LOSS_REASONS}
                empty="None"
              />
            </FieldBox>
          )}
          <FieldBox label="Notes" htmlFor="ep-notes" error={f.errors.notes} full>
            <input id="ep-notes" maxLength={255} {...f.text("notes")} />
          </FieldBox>
          <div className="full checks">
            <label className="check">
              <input
                type="checkbox"
                checked={v.commissionPaid}
                onChange={(e) => f.set("commissionPaid")(e.target.checked)}
              />
              Commission Paid
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={v.rewrite}
                onChange={(e) => f.set("rewrite")(e.target.checked)}
              />
              Rewrite
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={v.modified}
                onChange={(e) => f.set("modified")(e.target.checked)}
              />
              Modified?
            </label>
          </div>
        </div>
        {admin && (
          <fieldset className="admin-fields">
            <legend className="eyebrow">Commission corrections (admin)</legend>
            <div className="form-grid">
              <FieldBox label="Agent's Comp" htmlFor="ep-comp">
                <Select
                  id="ep-comp"
                  value={v.agentComp}
                  onChange={f.set("agentComp")}
                  options={COMMISSION_TIERS.map((t) => [String(t), `${t}%`] as const)}
                  empty="Not set"
                />
              </FieldBox>
              <FieldBox
                label="Deducted Amount"
                htmlFor="ep-damount"
                error={f.errors.deductedAmount}
              >
                <input id="ep-damount" inputMode="decimal" {...f.text("deductedAmount", "mono")} />
              </FieldBox>
              <label className="check full">
                <input
                  type="checkbox"
                  checked={v.deducted}
                  onChange={(e) => f.set("deducted")(e.target.checked)}
                />
                Deducted (charged back)
              </label>
            </div>
          </fieldset>
        )}
      </div>
      <div className="card-foot">
        <button type="button" className="btn" onClick={onDone}>
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          style={{ marginLeft: "auto" }}
          disabled={update.isPending}
        >
          {update.isPending ? "Saving…" : "Save"}
        </button>
      </div>
    </form>
  );
}

function StageHistory({ id }: { id: string }) {
  const stages = useStageHistory(id);
  return (
    <div className="card">
      <div className="card-head">
        <h3>Stage history</h3>
      </div>
      {stages.isError ? (
        <ErrorState error={stages.error} onRetry={() => void stages.refetch()} />
      ) : (
        <ul className="tl">
          {(stages.data ?? []).map((c) => (
            <li key={c.at + c.to} className="tl-item">
              <span className="tl-icon">
                <Icon name="chevR" />
              </span>
              <div>
                <div className="tl-head">
                  <b>
                    {c.from ? (
                      <>
                        {policyStageLabel(c.from)} → {policyStageLabel(c.to)}
                      </>
                    ) : (
                      <>Sold as {policyStageLabel(c.to)}</>
                    )}
                  </b>
                  <span className="when">{when(c.at)}</span>
                </div>
                <div className="tl-meta">
                  {c.by?.fullName ?? (c.via ? `${c.via} (integration)` : "System")}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
